import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchExamSkills, selectRandomSkills } from "../services/examApi";

/**
 * Async thunk to fetch skills from API and select random skills for exam
 */
export const fetchAndSelectSkills = createAsyncThunk(
  "exam/fetchAndSelectSkills",
  async ({ examId, count = 5 }, { rejectWithValue }) => {
    try {
      // Fetch all skills for this exam from API
      const allSkills = await fetchExamSkills(examId);

      // Select random skills (handwashing mandatory, avoid conflicts)
      const selectedSkills = selectRandomSkills(allSkills, count);

      return {
        allSkills,
        selectedSkills,
        examId,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch all skills for an exam without selecting any.
 * Used by the practice-mode skill picker.
 */
export const loadExamSkills = createAsyncThunk(
  "exam/loadExamSkills",
  async ({ examId }, { rejectWithValue }) => {
    try {
      const allSkills = await fetchExamSkills(examId);
      return { allSkills, examId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Evaluator flow
  evaluatorPhase: "welcome", // welcome, understand, generating, ready, picking

  // Exam mode
  examMode: "full", // full (5 skills, 3 credits) | practice (1 skill, 1 credit)

  // Competition context — tags the resulting ExamAttempt when launched from a
  // competition's Compete CTA. null = not part of a competition.
  competitionId: null,

  // The skill the competition is scored on. When set, the practice-mode skill
  // picker auto-selects it so the student goes straight into the competition skill.
  competitionSkillId: null,

  // Exam metadata
  examId: null,
  examState: "Georgia",
  examProvider: "credentia",
  startedAt: null,
  completedAt: null,

  // Skills
  skills: [],
  allSkills: [], // All available skills from API
  currentSkillIndex: 0,

  // Loading states
  skillsLoading: false,
  skillsError: null,

  // Recordings - store blob URLs for each skill
  recordings: {}, // { skillId: { blobUrl, duration, recordedAt } }

  // Progress
  phase: "not_started", // not_started, intro, recording, between_skills, reviewing, submitting, results

  // Results
  results: null, // { skills: [{ skillId, score, passed, feedback }], overallScore, passed }
};

export const examSlice = createSlice({
  name: "exam",
  initialState,
  reducers: {
    // Evaluator flow actions
    setEvaluatorPhase: (state, action) => {
      state.evaluatorPhase = action.payload;
    },

    // Set exam mode (full | practice)
    setExamMode: (state, action) => {
      state.examMode = action.payload;
    },

    // Set the competition this attempt belongs to (or null to clear)
    setCompetitionId: (state, action) => {
      state.competitionId = action.payload;
    },

    // Set the skill the competition picker should auto-select (or null to clear)
    setCompetitionSkillId: (state, action) => {
      state.competitionSkillId = action.payload;
    },

    // Practice mode: select a single skill by id from allSkills
    selectPracticeSkill: (state, action) => {
      const skill = state.allSkills.find((s) => s.id === action.payload);
      state.skills = skill ? [skill] : state.allSkills.slice(0, 1);
    },

    // Legacy synchronous generateSkills (for backward compatibility during transition)
    generateSkills: (state, action) => {
      // This is now handled by fetchAndSelectSkills async thunk
      // Keeping for backward compatibility - will be called after async fetch completes
      state.examState = action.payload?.state || "Georgia";
      state.examProvider = action.payload?.provider || "credentia";
      state.evaluatorPhase = "ready";
    },

    // Start a new exam with randomized skills
    startExam: (state, action) => {
      state.examId = `exam-${Date.now()}`;
      state.examState = action.payload?.state || state.examState;
      state.examProvider = action.payload?.provider || state.examProvider;
      state.startedAt = new Date().toISOString();
      state.currentSkillIndex = 0;
      state.recordings = {};
      state.phase = "watching"; // Skip intro, go straight to first skill video
      state.results = null;
      state.completedAt = null;
    },

    // Move to recording phase for current skill
    startRecording: (state) => {
      state.phase = "recording";
    },

    // Save recording for current skill and move to next
    saveRecording: (state, action) => {
      const { blobUrl, duration } = action.payload;
      const currentSkill = state.skills[state.currentSkillIndex];

      state.recordings[currentSkill.id] = {
        blobUrl,
        duration,
        recordedAt: new Date().toISOString(),
      };

      // Check if this was the last skill
      if (state.currentSkillIndex >= state.skills.length - 1) {
        // Practice mode skips the review grid and submits immediately
        state.phase = state.examMode === "practice" ? "submitting" : "reviewing";
      } else {
        state.phase = "between_skills";
      }
    },

    // Move to next skill
    nextSkill: (state) => {
      if (state.currentSkillIndex < state.skills.length - 1) {
        state.currentSkillIndex += 1;
        state.phase = "watching"; // Go to video demo for next skill
      }
    },

    // Go back to a specific skill to re-record
    goToSkill: (state, action) => {
      const index = action.payload;
      if (index >= 0 && index < state.skills.length) {
        state.currentSkillIndex = index;
        state.phase = "watching"; // Go to video demo for that skill
      }
    },

    // Start submission process
    startSubmission: (state) => {
      state.phase = "submitting";
    },

    // Save results after AI grading
    setResults: (state, action) => {
      state.results = action.payload;
      state.completedAt = new Date().toISOString();
      state.phase = "results";
    },

    // Reset exam
    resetExam: () => initialState,

    // Set phase directly
    setPhase: (state, action) => {
      state.phase = action.payload;
    },

    // Clear skills error
    clearSkillsError: (state) => {
      state.skillsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAndSelectSkills.pending, (state) => {
        state.skillsLoading = true;
        state.skillsError = null;
      })
      .addCase(fetchAndSelectSkills.fulfilled, (state, action) => {
        state.skillsLoading = false;
        state.allSkills = action.payload.allSkills;
        state.skills = action.payload.selectedSkills;
        state.evaluatorPhase = "ready";
      })
      .addCase(fetchAndSelectSkills.rejected, (state, action) => {
        state.skillsLoading = false;
        state.skillsError = action.payload || "Failed to load skills";
      })
      .addCase(loadExamSkills.pending, (state) => {
        state.skillsLoading = true;
        state.skillsError = null;
      })
      .addCase(loadExamSkills.fulfilled, (state, action) => {
        state.skillsLoading = false;
        state.allSkills = action.payload.allSkills;
      })
      .addCase(loadExamSkills.rejected, (state, action) => {
        state.skillsLoading = false;
        state.skillsError = action.payload || "Failed to load skills";
      });
  },
});

export const {
  setEvaluatorPhase,
  setExamMode,
  setCompetitionId,
  setCompetitionSkillId,
  selectPracticeSkill,
  generateSkills,
  startExam,
  startRecording,
  saveRecording,
  nextSkill,
  goToSkill,
  startSubmission,
  setResults,
  resetExam,
  setPhase,
  clearSkillsError,
} = examSlice.actions;

// Selectors
export const selectExam = (state) => state.exam;
export const selectCurrentSkill = (state) => state.exam.skills[state.exam.currentSkillIndex];
export const selectProgress = (state) => ({
  current: state.exam.currentSkillIndex + 1,
  total: state.exam.skills.length,
  percentage: ((state.exam.currentSkillIndex + 1) / state.exam.skills.length) * 100,
});
export const selectRecordingCount = (state) => Object.keys(state.exam.recordings).length;
export const selectSkillsLoading = (state) => state.exam.skillsLoading;
export const selectSkillsError = (state) => state.exam.skillsError;

export default examSlice.reducer;
